import React, { FC, SyntheticEvent, useEffect, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
    faCheckDouble,
    faChevronDown,
    faChevronUp,
    faListCheck,
    faPlus
} from "@fortawesome/free-solid-svg-icons";
import {
    VariationCollapseState,
    VariationViewType
} from "../../../models/common";
import {
    Button,
    Card,
    Col,
    Collapse,
    Container,
    FormControl,
    InputGroup,
    OverlayTrigger,
    Row,
    Tooltip
} from "react-bootstrap";
import {
    MeData,
    OrganizationConfigData,
    PartialRevisionData,
    PartialRevisionVariables,
    Revision
} from "../../../models/types";
import { Networks } from "./Networks";
import {
    getVariationCollapseState,
    saveVariationCollapseState,
    updateRevisionPartially,
    validateBuildSetName
} from "../../../common/Helpers";
import { useApolloClient, useMutation, useQuery } from "@apollo/client";
import { PARTIAL_UPDATE_REVISION } from "../../../graphql/mutations";
import { GET_ME, GET_ORGANIZATION_CONFIG } from "../../../graphql/queries";

interface Props {
    revision: Revision;
    openNewItemModal: (type: VariationViewType) => void;
}

const NetworksManagerImplementation: FC<Props> = ({
    revision,
    openNewItemModal
}) => {
    const client = useApolloClient();
    const { data: { me } = {} } = useQuery<MeData>(GET_ME, {
        fetchPolicy: "cache-only"
    });
    const [showNetworks, setShowNetworks] = useState(
        getVariationCollapseState(VariationCollapseState.Networks)
    );
    const [defaultNetworkOverride, setDefaultNetworkOverride] = useState("");
    const [orgAnalyticsChance, setOrgAnalyticsChance] = useState("");
    const [partialUpdateRevision] = useMutation<
        PartialRevisionData,
        PartialRevisionVariables
    >(PARTIAL_UPDATE_REVISION);

    const { data: { organizationConfig } = {} } =
        useQuery<OrganizationConfigData>(GET_ORGANIZATION_CONFIG, {
            variables: {
                organizationId: me?.organization?.id
            }
        });

    useEffect(() => {
        if (organizationConfig?.useAnalyticsChance) {
            const newChance = organizationConfig?.useAnalyticsChance * 100;
            setOrgAnalyticsChance(newChance.toFixed(2));
        }
    }, [organizationConfig?.useAnalyticsChance]);

    useEffect(() => {
        if (revision.buildData?.defaultNetworkOverride)
            setDefaultNetworkOverride(
                revision.buildData.defaultNetworkOverride
            );
    }, [revision.buildData?.defaultNetworkOverride]);

    const updateUseAnalytics = async (useAnalytics: boolean) => {
        if (revision) {
            await updateRevisionPartially(
                client,
                revision,
                partialUpdateRevision,
                { analytics: useAnalytics }
            );
        }
    };

    const updateAnalyticsPercentage = async (
        useAnalyticsChance: number | null
    ) => {
        if (revision) {
            let newChance = useAnalyticsChance;
            if (newChance !== null) {
                const clamped = Math.max(0, Math.min(100, Number(newChance)));
                newChance =
                    Math.round((clamped / 100 + Number.EPSILON) * 10000) /
                    10000;
            }
            await updateRevisionPartially(
                client,
                revision,
                partialUpdateRevision,
                {
                    useAnalyticsChance: newChance
                }
            );
        }
    };

    const updateDefaultNetworkOverride = async () => {
        if (revision) {
            await updateRevisionPartially(
                client,
                revision,
                partialUpdateRevision,
                { defaultNetworkOverride: defaultNetworkOverride }
            );
        }
    };

    const selectAllNetworks = async (all: boolean) => {
        if (revision && revision.buildData) {
            const newNetworks = [...revision.buildData.networks];
            for (let i = 0; i < newNetworks.length; i++) {
                const sizeTargets = all ? [2, 3, 5] : [];
                newNetworks[i] = { ...newNetworks[i], sizeTargets };
            }
            const newBuildData = {
                networks: newNetworks
            };
            await updateRevisionPartially(
                client,
                revision,
                partialUpdateRevision,
                newBuildData
            );
        }
    };

    const updateShowNetworks = (newState: boolean) => {
        saveVariationCollapseState(VariationCollapseState.Networks, newState);
        setShowNetworks(newState);
    };

    const selectReviewNetworks = async () => {
        if (revision && revision.buildData) {
            const newNetworks = [...revision.buildData.networks];
            for (let i = 0; i < newNetworks.length; i++) {
                const sizeTargets =
                    newNetworks[i].network === "unity" ? [2, 3, 5] : [];
                newNetworks[i] = { ...newNetworks[i], sizeTargets };
            }
            const newBuildData = {
                networks: newNetworks
            };
            await updateRevisionPartially(
                client,
                revision,
                partialUpdateRevision,
                newBuildData
            );
        }
    };
    const hasNetworks =
        revision.buildData &&
        Array.isArray(revision.buildData.networks) &&
        revision.buildData.networks.length > 0;

    return (
        <>
            <Container className="mb-4 p-0">
                <Card>
                    <Card.Header className="d-flex align-items-center">
                        <h5 className="text-inverse-50 mb-1 me-2">Networks</h5>
                        {hasNetworks && (
                            <div
                                onClick={() => {
                                    updateShowNetworks(!showNetworks);
                                }}
                                data-toggle="tooltip"
                                title="Toggle networks visibility"
                                className="d-flex ms-1 align-items-center mouseHover text-inverse-50"
                            >
                                <FontAwesomeIcon
                                    icon={
                                        showNetworks
                                            ? faChevronUp
                                            : faChevronDown
                                    }
                                />
                            </div>
                        )}
                        <div className="float-end ms-auto">
                            <Button
                                onClick={() =>
                                    openNewItemModal(VariationViewType.Networks)
                                }
                                className="btn-sm me-2 mouseHover"
                                data-toggle="tooltip"
                                title="Add new network"
                            >
                                <FontAwesomeIcon icon={faPlus} size="lg" />
                            </Button>
                            <Button
                                variant="primary"
                                size="sm"
                                onClick={() => selectReviewNetworks()}
                                data-toggle="tooltip"
                                title="Select review set of networks"
                                disabled={!hasNetworks}
                            >
                                <FontAwesomeIcon icon={faListCheck} />
                            </Button>
                            <Button
                                variant="success"
                                size="sm"
                                className="ms-2"
                                onClick={() => selectAllNetworks(true)}
                                data-toggle="tooltip"
                                title="Select all networks"
                                disabled={!hasNetworks}
                            >
                                <FontAwesomeIcon icon={faCheckDouble} />
                            </Button>
                            <Button
                                variant="danger"
                                size="sm"
                                className="ms-2"
                                onClick={() => selectAllNetworks(false)}
                                data-toggle="tooltip"
                                title="Remove all network selections"
                                disabled={!hasNetworks}
                            >
                                <FontAwesomeIcon icon={faCheckDouble} />
                            </Button>
                        </div>
                    </Card.Header>
                    {hasNetworks && (
                        <Collapse in={showNetworks}>
                            <div>
                                <Card.Body className="mb-2">
                                    <Row className="justify-content-between mx-auto">
                                        <Col md="auto" lg="auto" xl={3}>
                                            <InputGroup
                                                style={{
                                                    minWidth: "166px",
                                                    maxWidth: "166px"
                                                }}
                                                className="mb-2"
                                                onClick={(
                                                    event: SyntheticEvent
                                                ) => {
                                                    event.stopPropagation();
                                                }}
                                            >
                                                <InputGroup.Checkbox
                                                    title="Enable analytics"
                                                    aria-label="Enable analytics"
                                                    checked={
                                                        revision.buildData
                                                            ?.analytics || false
                                                    }
                                                    onChange={(event: any) => {
                                                        updateUseAnalytics(
                                                            event.target.checked
                                                        );
                                                    }}
                                                />
                                                <InputGroup.Text>
                                                    Use Analytics
                                                </InputGroup.Text>
                                            </InputGroup>
                                        </Col>
                                        {revision.buildData?.analytics ? (
                                            <Col md="auto" lg={5} xl="auto">
                                                {revision.buildData
                                                    ?.useAnalyticsChance !==
                                                    undefined &&
                                                revision.buildData
                                                    ?.useAnalyticsChance !==
                                                    null &&
                                                revision.buildData
                                                    ?.useAnalyticsChance >=
                                                    0 ? (
                                                    <div
                                                        style={{
                                                            minWidth: "340px",
                                                            maxWidth: "350px"
                                                        }}
                                                    >
                                                        <InputGroup className="mb-2">
                                                            <InputGroup.Text>
                                                                Analytics Chance
                                                                (%)
                                                            </InputGroup.Text>
                                                            <FormControl
                                                                type="number"
                                                                min={0}
                                                                max={100}
                                                                step={0.01}
                                                                className="analyticsChanceInput"
                                                                disabled={
                                                                    !revision
                                                                        .buildData
                                                                        ?.analytics
                                                                }
                                                                defaultValue={
                                                                    revision
                                                                        .buildData
                                                                        ?.useAnalyticsChance *
                                                                    100
                                                                }
                                                                onBlur={(
                                                                    event: any
                                                                ) => {
                                                                    const raw =
                                                                        event.target.value.trim();
                                                                    const value =
                                                                        Number(
                                                                            raw
                                                                        );
                                                                    const hasMax2Decimals =
                                                                        /^\d+(\.\d{1,2})?$/.test(
                                                                            String(
                                                                                raw
                                                                            )
                                                                        );
                                                                    if (
                                                                        raw !==
                                                                            "" &&
                                                                        !isNaN(
                                                                            value
                                                                        ) &&
                                                                        value >=
                                                                            0 &&
                                                                        value <=
                                                                            100 &&
                                                                        hasMax2Decimals
                                                                    ) {
                                                                        updateAnalyticsPercentage(
                                                                            value
                                                                        );
                                                                    }
                                                                }}
                                                            />
                                                            <Button
                                                                variant="danger"
                                                                onClick={() => {
                                                                    updateAnalyticsPercentage(
                                                                        null
                                                                    );
                                                                }}
                                                            >
                                                                Remove
                                                            </Button>
                                                        </InputGroup>
                                                    </div>
                                                ) : organizationConfig?.useAnalyticsChance ? (
                                                    <div
                                                        style={{
                                                            minWidth: "335px",
                                                            maxWidth: "350px"
                                                        }}
                                                    >
                                                        <OverlayTrigger
                                                            placement="top"
                                                            delay={{
                                                                show: 150,
                                                                hide: 300
                                                            }}
                                                            overlay={
                                                                <Tooltip>
                                                                    Analytics
                                                                    chance
                                                                    applied from
                                                                    organization
                                                                    config
                                                                    (Overridable
                                                                    locally)
                                                                </Tooltip>
                                                            }
                                                        >
                                                            <InputGroup className="mb-2">
                                                                <InputGroup.Text>
                                                                    Analytics
                                                                    Chance (%)
                                                                </InputGroup.Text>
                                                                <FormControl
                                                                    type="number"
                                                                    min={0}
                                                                    max={100}
                                                                    step={0.01}
                                                                    className="analyticsChanceInput"
                                                                    disabled
                                                                    value={
                                                                        orgAnalyticsChance
                                                                    }
                                                                />
                                                                <Button
                                                                    variant="warning"
                                                                    onClick={() => {
                                                                        updateAnalyticsPercentage(
                                                                            10
                                                                        );
                                                                    }}
                                                                >
                                                                    Override
                                                                </Button>
                                                            </InputGroup>
                                                        </OverlayTrigger>
                                                    </div>
                                                ) : (
                                                    <Button
                                                        className="mb-2"
                                                        onClick={() => {
                                                            updateAnalyticsPercentage(
                                                                10
                                                            );
                                                        }}
                                                    >
                                                        Set Analytics Chance
                                                    </Button>
                                                )}
                                            </Col>
                                        ) : null}
                                        <Col md="auto" lg={4} xl={4}>
                                            <InputGroup
                                                style={{
                                                    minWidth: "250px",
                                                    maxWidth: "350px"
                                                }}
                                            >
                                                <InputGroup.Text>
                                                    Network Override
                                                </InputGroup.Text>
                                                <FormControl
                                                    value={
                                                        defaultNetworkOverride ||
                                                        ""
                                                    }
                                                    onChange={(event: any) => {
                                                        const newName =
                                                            event.target.value.replace(
                                                                " ",
                                                                ""
                                                            );
                                                        if (
                                                            validateBuildSetName(
                                                                newName,
                                                                true
                                                            )
                                                        ) {
                                                            setDefaultNetworkOverride(
                                                                newName
                                                            );
                                                        }
                                                    }}
                                                    onBlur={() => {
                                                        updateDefaultNetworkOverride();
                                                    }}
                                                />
                                            </InputGroup>
                                        </Col>
                                    </Row>
                                    <Networks revision={revision} />
                                </Card.Body>
                            </div>
                        </Collapse>
                    )}
                </Card>
            </Container>
        </>
    );
};

export const NetworksManager = React.memo(NetworksManagerImplementation);
